/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useTransitionStyles,
} from '@floating-ui/react';
import { tokens } from '@neo4j-ndl/base';
import classNames from 'classnames';
import type React from 'react';
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { type PolymorphicCommonProps } from '../_common/types';
import { ConditionalWrap } from '../conditional-wrap';
import { useIsInsideDialog } from '../dialog/dialog-context';
import { ClockIconOutline, ExclamationCircleIconSolid } from '../icons';
import { useNeedleTheme } from '../theme';
import { Typography } from '../typography';
import { generateTimeOptions } from './generate-time-options';
import { NeedleTime } from './needle-time';
import {
  useKeyboardNavigation,
  useTimePickerPopover,
} from './time-picker-hooks';

export type TimeFormat = 'hh:mm' | 'hh:mm aa';

export type TimePickerProps = {
  /** The value of the time picker */
  value?: NeedleTime;
  /** The format of the time picker */
  format?: TimeFormat;
  /** Whether the time picker is fluid */
  isFluid?: boolean;
  /** Whether the time picker is read only */
  isReadOnly?: boolean;
  /** Whether the time picker is required */
  isRequired?: boolean;
  /** Whether the time picker is disabled */
  isDisabled?: boolean;
  /** The time interval of the time picker, in minutes */
  timeInterval?: number;
  /** The label of the time picker */
  label?: string;
  /** The size of the time picker */
  size?: 'small' | 'medium' | 'large';
  /** Manually set the error text */
  errorText?: string;
  /** Callback function triggered when the time picker value changes */
  onChange?: (time: NeedleTime) => void;
  /** Callback function triggered when the time picker encounters an error */
  onError?: (error: string, time: NeedleTime) => void;
  /**
   * Strategy for the dropdown floating element.
   * By default it is absolute, but if the component is inside of a needle Dialog, it is set to fixed.
   * If this prop is set, no auto-detection of Dialog is done.
   */
  floatingStrategy?: 'absolute' | 'fixed';
  /** Whether the dropdown element is rendered in a portal. */
  isPortaled?: boolean;
};

export const TimePicker = <T extends React.ElementType = 'div'>({
  format = 'hh:mm',
  isDisabled,
  isFluid,
  isReadOnly,
  isRequired,
  value,
  timeInterval = 15,
  label,
  onChange,
  className,
  onError,
  style,
  size = 'medium',
  errorText,
  htmlAttributes,
  floatingStrategy,
  isPortaled: isPortaledProp,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, TimePickerProps>) => {
  const [editedInput, setEditedInput] = useState<NeedleTime | undefined>(value);

  const is24Hour = format === 'hh:mm';
  const [inputValue, setInputValue] = useState<string>(
    value?.toString(is24Hour) ?? '',
  );
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => inputRef.current as HTMLInputElement, []);
  const [lastAction, setLastAction] = useState<'type' | 'arrow'>('type');
  const [error, setError] = useState<string | undefined>(undefined);

  const timeOptions = useMemo(
    () => generateTimeOptions(is24Hour, timeInterval),
    [is24Hour, timeInterval],
  );

  const { themeClassName } = useNeedleTheme();
  const { isPopoverOpen, setIsPopoverOpen } = useTimePickerPopover(false);
  const { focusedIndex, setFocusedIndex, handleArrowNavigation } =
    useKeyboardNavigation(listRef, timeOptions.length);

  const classes = {
    'ndl-small': size === 'small',
    'ndl-medium': size === 'medium',
    'ndl-large': size === 'large',
    'ndl-error': error !== undefined,
    'ndl-fluid': isFluid,
    'ndl-disabled': isDisabled,
    'ndl-read-only': isReadOnly,
  };

  const inputWidth =
    isFluid === true ? '100%' : is24Hour === true ? '6rem' : '9rem';

  const errorToShow =
    errorText !== undefined && errorText !== '' ? errorText : error;

  const isValidTime = useCallback((hours: number, minutes: number) => {
    if (hours > 23 || hours < 0) {
      return false;
    }

    if (minutes > 59 || minutes < 0) {
      return false;
    }

    return true;
  }, []);

  useEffect(() => {
    if (error !== undefined) {
      onError?.(error, NeedleTime.fromString(inputValue));
    }
  }, [error, inputValue, onError]);

  useEffect(() => {
    setEditedInput(value);
    setInputValue(value?.toString(is24Hour) ?? '');
  }, [is24Hour, value]);

  useEffect(() => {
    if (isPopoverOpen) {
      setTimeout(() => {
        listRef.current?.children[focusedIndex]?.scrollIntoView({
          block: 'center',
          inline: 'nearest',
        });
      }, 0);
    } else {
      setTimeout(() => {
        inputRef.current?.blur();
        const selectedIndex =
          timeOptions.indexOf(value?.toString(is24Hour) ?? '') ?? 0;
        setFocusedIndex(selectedIndex);
      }, 0);
    }
  }, [
    focusedIndex,
    is24Hour,
    isPopoverOpen,
    value,
    setFocusedIndex,
    timeOptions,
  ]);

  const handleOnClick = (timeText: string) => {
    if (onChange) {
      onChange(NeedleTime.fromString(timeText));
    }
    setError(undefined);
    setInputValue(timeText);
    setEditedInput(NeedleTime.fromString(timeText));
    setIsPopoverOpen(false);
  };
  const newHandleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    scrollToTime(e.target.value);
    setInputValue(e.target.value);
    setLastAction('type');

    const time = NeedleTime.fromString(e.target.value);
    if (!isValidTime(time.hour, time.minute)) {
      setError('Invalid time');
    } else {
      setError(undefined);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isReadOnly === true || isDisabled === true) {
      e.preventDefault();
      return;
    }

    if (e.key === 'Escape') {
      setIsPopoverOpen(false);
    }

    setIsPopoverOpen(true);

    if (e.key === 'Enter') {
      if (lastAction === 'type' && inputValue.length >= (is24Hour ? 5 : 6)) {
        const time = NeedleTime.fromString(inputValue);
        handleOnClick(time.toString(is24Hour));
      } else if (lastAction === 'type' && !inputValue.includes(':')) {
        const time = NeedleTime.fromString(inputValue);
        if (isValidTime(time.hour, time.minute)) {
          handleOnClick(time.toString(is24Hour));
        }
        setIsPopoverOpen(false);
      } else {
        handleOnClick(timeOptions[focusedIndex]);
      }
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      setLastAction('arrow');
      handleArrowNavigation(e.key);
    }
  };

  const scrollToTime = (startOfTime: string) => {
    const parsedTime = startOfTime.toLowerCase().replace(/[^0-9amp]/g, '');
    let index = -1;

    // Handle AM/PM input
    const isPM = parsedTime.includes('p');
    const isAM = parsedTime.includes('a');
    const timeDigits = parsedTime.replace(/[amp]/g, '');

    if (timeDigits.length === 1) {
      // Single digit - assume it's an hour
      const hour = `0${timeDigits}`;
      const searchTime = is24Hour
        ? `${hour}:00`
        : `${hour}:00 ${isPM ? 'PM' : 'AM'}`;
      index = timeOptions.indexOf(searchTime);
    } else {
      // Find closest matching time
      const closestTime = timeOptions.find((time) => {
        const timeWithoutSpecialChars = time
          .toLowerCase()
          .replace(/[^0-9amp]/g, '');
        if (isPM || isAM) {
          // If AM/PM specified, match that specifically
          return timeWithoutSpecialChars.startsWith(parsedTime);
        } else {
          // Otherwise just match the numbers
          return timeWithoutSpecialChars
            .replace(/[amp]/g, '')
            .startsWith(timeDigits);
        }
      });
      index = closestTime !== undefined ? timeOptions.indexOf(closestTime) : -1;
    }

    if (index !== -1) {
      setFocusedIndex(index);
    }

    return index;
  };

  /** Custom floating ui solution */
  const isInsideDialog = useIsInsideDialog();
  const isPortaled = isPortaledProp ?? !isInsideDialog;
  const containerRef = useRef<HTMLDivElement>(null);
  const { refs, floatingStyles, context } = useFloating({
    elements: {
      reference: containerRef.current,
    },
    middleware: [
      offset(10),
      flip({
        fallbackPlacements: ['top'],
        fallbackStrategy: 'bestFit',
      }),
      shift({ padding: 5 }),
    ],
    onOpenChange: (open) => {
      setIsPopoverOpen(open);
    },
    open: isDisabled !== true && isReadOnly !== true && isPopoverOpen,
    placement: 'bottom',
    strategy: floatingStrategy ?? (isInsideDialog ? 'fixed' : 'absolute'),
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);
  const { styles: transitionStyles } = useTransitionStyles(context, {
    duration: Number.parseInt(tokens.motion.duration.quick) ?? 0,
  });

  const mergedRef = useMergeRefs([refs.setReference, containerRef]);

  return (
    <>
      <div
        className={classNames('ndl-time-picker', className, {
          ...classes,
        })}
        ref={mergedRef}
        {...getReferenceProps()}
        style={{
          width: isFluid === true ? '100%' : 'fit-content',
        }}
      >
        <label className="ndl-time-picker-label">
          {label !== undefined && (
            <Typography
              variant={size === 'large' ? 'body-large' : 'body-medium'}
            >
              {label}
            </Typography>
          )}
          <div
            className="ndl-time-picker-input-wrapper"
            style={{
              width: inputWidth,
              ...style,
            }}
          >
            <input
              aria-label={label} // TODO: handle label natively
              className="ndl-time-picker-input"
              type="text"
              ref={inputRef}
              value={inputValue}
              disabled={isDisabled}
              readOnly={isReadOnly}
              required={isRequired}
              onChange={newHandleInputChange}
              onKeyDown={handleKeyDown}
              onClick={() => {
                if (isReadOnly === true || isDisabled === true) {
                  return;
                }
                setIsPopoverOpen(true);
              }}
              onBlur={(e) => {
                if (isReadOnly === true || isDisabled === true) {
                  return;
                }

                if (
                  e.relatedTarget?.classList.contains(
                    'ndl-time-picker-popover-item',
                  ) === true
                ) {
                  return;
                }

                if (inputValue.length < 4) {
                  setError('Invalid time');
                  return;
                }

                const time = NeedleTime.fromString(inputValue);

                if (time === undefined || time === null) {
                  setError('Invalid time');
                  return;
                }

                if (!isValidTime(time.hour, time.minute)) {
                  setError('Invalid time');
                } else {
                  setError(undefined);
                  setInputValue(time.toString(is24Hour));
                }
              }}
              placeholder={format}
              maxLength={is24Hour ? 5 : 8}
              {...restProps}
              {...htmlAttributes}
            />
            <ClockIconOutline className="ndl-icon-svg ndl-time-picker-icon" />
          </div>
        </label>
        {errorToShow !== undefined && (
          <div className="ndl-time-picker-error-wrapper">
            <ExclamationCircleIconSolid className="ndl-time-picker-error-icon" />
            <Typography
              variant={size === 'large' ? 'body-medium' : 'body-small'}
              className="ndl-time-picker-error-text"
            >
              {errorToShow}
            </Typography>
          </div>
        )}
      </div>

      {context.open && (
        <ConditionalWrap
          shouldWrap={isPortaled}
          wrap={(wrapChildren) => (
            <FloatingPortal>{wrapChildren}</FloatingPortal>
          )}
        >
          <FloatingFocusManager
            context={context}
            modal={false}
            initialFocus={-1}
          >
            <div
              ref={refs.setFloating}
              className={classNames(themeClassName, 'ndl-time-picker-popover', {
                ...classes,
              })}
              style={{
                ...transitionStyles,
                ...floatingStyles,
                width:
                  isFluid === true
                    ? containerRef.current?.clientWidth
                    : undefined,
              }}
              {...getFloatingProps()}
            >
              <ul ref={listRef} tabIndex={-1}>
                {timeOptions.map((time, i) => (
                  <li
                    key={i}
                    role="option"
                    aria-selected={
                      value?.toString(is24Hour) === time ||
                      editedInput?.toString(is24Hour) === time
                    }
                    value={time}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleOnClick(time);
                      setIsPopoverOpen(false);
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    tabIndex={-1}
                    className={classNames(
                      focusedIndex === i ? 'focused' : '',
                      'ndl-time-picker-popover-item',
                    )}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                        handleOnClick(time);
                      }
                    }}
                  >
                    {time}
                  </li>
                ))}
              </ul>
            </div>
          </FloatingFocusManager>
        </ConditionalWrap>
      )}
    </>
  );
};
